import { ChecklistItem } from './buildChecklistItems';

/** Fallbacks for documents that arrive without a resolved platform folder. */
const UNKNOWN_FOLDER_ID = 'unknown';
const UNKNOWN_FOLDER_NAME = 'Unknown Folder';

/**
 * One folder section for the non-SkySlope (Dotloop, ZipForm) "Other documents"
 * grouping: the platform folder plus the items that live in it.
 */
export interface FolderGroup {
  /** Platform folder id (grouping key); `'unknown'` when the doc has none. */
  folderId: string;
  /** Folder display name; "Unknown Folder" when the doc has none. */
  folderName: string;
  /** The items in this folder, in incoming order. */
  items: ChecklistItem[];
}

/**
 * Groups agent-review items by their platform folder, mirroring how the
 * extension groups Dotloop/ZipForm documents by folder
 * (`groupIngestionDocumentsByFolder`) — but on this page's lighter
 * {@link ChecklistItem} shape, so the internal/external surfaces stay
 * decoupled. Folder grouping only *supplements* the needs-review/other review
 * split; callers group each bucket independently rather than letting folders
 * replace that split.
 *
 * Each item is single-document on non-SkySlope platforms (no checklist
 * activity), so the folder is read from the item's first document
 * (`folderId`/`folderName`). Folder order follows first appearance in the
 * incoming (most-recent-first) item order.
 */
export const groupItemsByFolder = (items: ChecklistItem[]): FolderGroup[] => {
  const groupsById = new Map<string, FolderGroup>();
  // Preserves first-appearance folder order (the item list is most-recent-first).
  const order: string[] = [];

  for (const item of items) {
    const doc = item.docs[0];
    const folderId = doc?.folderId ?? UNKNOWN_FOLDER_ID;
    const folderName = doc?.folderName ?? UNKNOWN_FOLDER_NAME;

    let group = groupsById.get(folderId);
    if (group == null) {
      group = { folderId, folderName, items: [] };
      groupsById.set(folderId, group);
      order.push(folderId);
    }

    group.items.push(item);
  }

  return order.map((folderId) => groupsById.get(folderId)!);
};

/** The folder name an item belongs to, for the per-row needs-review caption. */
export const folderNameForItem = (item: ChecklistItem): string | undefined =>
  item.docs[0]?.folderName ?? undefined;
